import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import StarsIcon from "@material-ui/icons/Stars";
import SubjectIcon from "@material-ui/icons/Subject";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import NoteAddOutlinedIcon from "@material-ui/icons/NoteAddOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";

/* ------------------------------------------------------------------ */
/* Status do copiloto — cache por sessão (uma chamada por aba)         */
/* ------------------------------------------------------------------ */

let statusCache = null; // { enabled, hasKey, model, reason }
let statusPromise = null;

export const resetCopilotStatusCache = () => {
  statusCache = null;
  statusPromise = null;
};

const fetchCopilotStatus = () => {
  if (statusCache) return Promise.resolve(statusCache);
  if (!statusPromise) {
    statusPromise = api
      .get("/copilot/status")
      .then(({ data }) => {
        statusCache = {
          enabled: !!(data && data.enabled),
          hasKey: !!(data && data.hasKey),
          model: (data && data.model) || null,
          reason: (data && data.reason) || null,
        };
        return statusCache;
      })
      .catch(() => {
        // backend antigo / rota ausente: copiloto indisponível nesta sessão
        statusCache = { enabled: false, hasKey: false, model: null, reason: "unavailable" };
        return statusCache;
      });
  }
  return statusPromise;
};

/**
 * useCopilotStatus() → { enabled, hasKey, model, reason, loading }
 * Resultado cacheado por sessão; `resetCopilotStatusCache()` força nova leitura
 * (ex.: após salvar a chave nas configurações).
 */
export const useCopilotStatus = () => {
  const [status, setStatus] = useState(statusCache);
  const [loading, setLoading] = useState(!statusCache);

  useEffect(() => {
    let active = true;
    fetchCopilotStatus().then((s) => {
      if (!active) return;
      setStatus(s);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return {
    enabled: !!(status && status.enabled),
    hasKey: !!(status && status.hasKey),
    model: status ? status.model : null,
    reason: status ? status.reason : null,
    loading,
  };
};

/* ------------------------------------------------------------------ */
/* Ações                                                               */
/* ------------------------------------------------------------------ */

const INSTRUCTIONS = [
  { key: "default", value: undefined },
  { key: "shorter", value: "shorter" },
  { key: "formal", value: "formal" },
  { key: "invoice", value: "explain_invoice" },
];

const insertIntoComposer = (message) => {
  window.dispatchEvent(
    new CustomEvent("insertQuickMessage", {
      detail: { quickMessage: { message } },
    })
  );
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    menuPaper: {
      minWidth: 220,
      marginTop: 4,
    },
    menuHeader: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: t.textMuted,
      padding: "8px 16px 4px",
    },
    menuItem: {
      fontSize: 13.5,
      minHeight: 38,
      gap: 10,
      color: t.text,
      "&:hover": { backgroundColor: t.muted },
    },
    menuIcon: {
      fontSize: 18,
      color: t.textSecondary,
      flexShrink: 0,
    },
    menuDivider: {
      margin: "4px 0",
      backgroundColor: t.line,
    },
    spinner: {
      color: "inherit",
    },
    summary: {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontSize: 14,
      lineHeight: 1.6,
      color: t.text,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      padding: "14px 16px",
      margin: 0,
      fontFamily: "inherit",
      maxHeight: "55vh",
      overflowY: "auto",
    },
    loadingBox: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "24px 8px",
      color: t.textSecondary,
      fontSize: 14,
    },
    hint: {
      marginTop: 10,
      fontSize: 12,
      color: t.textMuted,
    },
  };
});

/* ------------------------------------------------------------------ */
/* Dialog de resumo                                                    */
/* ------------------------------------------------------------------ */

/**
 * <CopilotSummaryDialog open ticketId onClose />
 * Ao abrir gera o resumo (persist=false); "Salvar como nota" grava (persist=true).
 */
export const CopilotSummaryDialog = ({ open, ticketId, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState("");
  const [savedNoteId, setSavedNoteId] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open || !ticketId) return;
    let active = true;
    setSummary("");
    setSavedNoteId(null);
    setLoading(true);
    api
      .post("/copilot/summarize", { ticketId, persist: false })
      .then(({ data }) => {
        if (!active) return;
        setSummary((data && data.summary) || "");
      })
      .catch((err) => {
        if (!active) return;
        toastError(err);
        onClose();
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(summary);
      } else {
        const el = document.createElement("textarea");
        el.value = summary;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      toast.success(i18n.t("copilot.toasts.copied"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/copilot/summarize", {
        ticketId,
        persist: true,
      });
      if (!mounted.current) return;
      if (data && data.summary) setSummary(data.summary);
      setSavedNoteId((data && data.noteId) || true);
      toast.success(i18n.t("copilot.toasts.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading || saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {i18n.t("copilot.dialog.title")}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress size={20} />
            <span>{i18n.t("copilot.dialog.loading")}</span>
          </div>
        ) : (
          <>
            <pre className={classes.summary}>
              {summary || i18n.t("copilot.dialog.empty")}
            </pre>
            <Typography className={classes.hint}>
              {i18n.t("copilot.dialog.hint")}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={saving}
        >
          {i18n.t("copilot.dialog.close")}
        </Button>
        <Button
          onClick={handleCopy}
          disabled={loading || !summary}
          variant="outlined"
          startIcon={<FileCopyOutlinedIcon />}
        >
          {i18n.t("copilot.dialog.copy")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading || saving || !summary || !!savedNoteId}
          variant="contained"
          color="primary"
          startIcon={
            saving ? (
              <CircularProgress size={16} className={classes.spinner} />
            ) : (
              <NoteAddOutlinedIcon />
            )
          }
        >
          {savedNoteId
            ? i18n.t("copilot.dialog.savedLabel")
            : i18n.t("copilot.dialog.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Item de menu "Resumir atendimento" (para o menu ⋮ do ticket)        */
/* ------------------------------------------------------------------ */

export const CopilotSummarizeMenuItem = React.forwardRef(
  ({ onSummarize, className, iconClassName, ...rest }, ref) => {
    const classes = useStyles();
    return (
      <MenuItem
        ref={ref}
        className={className || classes.menuItem}
        onClick={onSummarize}
        {...rest}
      >
        <SubjectIcon className={iconClassName || classes.menuIcon} />
        {i18n.t("copilot.summarize")}
      </MenuItem>
    );
  }
);

/* ------------------------------------------------------------------ */
/* Botão "Sugerir resposta"                                            */
/* ------------------------------------------------------------------ */

/**
 * <CopilotButton ticket className labelClassName onSummarize />
 * - Menu com instruções rápidas → POST /copilot/suggest → insere no composer.
 * - `onSummarize` (opcional): adiciona item "Resumir atendimento" ao menu.
 * A visibilidade (status.enabled + plano) é decidida por quem renderiza.
 */
const CopilotButton = ({ ticket, className, labelClassName, onSummarize, size = "small" }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSuggest = useCallback(
    async (instruction) => {
      handleClose();
      if (!ticket || !ticket.id) return;
      setLoading(true);
      try {
        const payload = { ticketId: ticket.id };
        if (instruction) payload.instruction = instruction;
        const { data } = await api.post("/copilot/suggest", payload);
        const suggestion = data && data.suggestion ? String(data.suggestion) : "";
        if (!suggestion.trim()) {
          toast.warn(i18n.t("copilot.toasts.empty"));
          return;
        }
        insertIntoComposer(suggestion);
        toast.info(i18n.t("copilot.toasts.inserted"));
      } catch (err) {
        toastError(err);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [ticket]
  );

  const handleSummarize = () => {
    handleClose();
    if (onSummarize) onSummarize();
  };

  return (
    <>
      <Tooltip title={i18n.t("copilot.tooltip")}>
        <span>
          <Button
            size={size}
            variant="outlined"
            className={className}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} className={classes.spinner} />
              ) : (
                <StarsIcon />
              )
            }
            onClick={handleOpen}
            aria-haspopup="true"
            aria-controls="copilot-menu"
          >
            <span className={labelClassName}>
              {loading
                ? i18n.t("copilot.loading")
                : i18n.t("copilot.button")}
            </span>
          </Button>
        </span>
      </Tooltip>
      <Menu
        id="copilot-menu"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        classes={{ paper: classes.menuPaper }}
      >
        <Typography component="div" className={classes.menuHeader}>
          {i18n.t("copilot.instructions.title")}
        </Typography>
        {INSTRUCTIONS.map((item) => (
          <MenuItem
            key={item.key}
            className={classes.menuItem}
            onClick={() => handleSuggest(item.value)}
          >
            {i18n.t(`copilot.instructions.${item.key}`)}
          </MenuItem>
        ))}
        {onSummarize && <Divider className={classes.menuDivider} />}
        {onSummarize && (
          <MenuItem className={classes.menuItem} onClick={handleSummarize}>
            <SubjectIcon className={classes.menuIcon} />
            {i18n.t("copilot.summarize")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CopilotButton;
